import { motion } from 'framer-motion'
import { Card, CardContent, Button } from '@mui/material'
import { Link } from 'react-router-dom'
import SEO from '../utils/seo'
import { colors } from '../styles/theme'

const About = () => {
  return (
    <>
      <SEO
        title="About"
        description="Learn about Prompted Thoughts, a developer-centric video podcast exploring the intersection of Gen AI, app development, developer experience, and data."
      />

      <div className="section">
        <div className="container-custom max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-off-white mb-8">About the Show</h1>

            <div className="prose prose-lg prose-invert max-w-none mb-12">
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                <strong className="text-off-white">Prompted Thoughts</strong> is a developer-centric
                video podcast at the intersection of Generative AI, app development, developer experience,
                and data. Each episode revolves around a single prompt—then goes deep on design,
                architecture, and the practical realities of shipping AI-native software.
              </p>

              <p className="text-gray-300 leading-relaxed mb-6">
                We cut through the hype to explore what it really takes to build production AI
                systems. Watch full video episodes on YouTube with visual demos and code walkthroughs,
                or listen to audio versions on your favorite podcast platform. From evals and agents to
                data quality and cost control, every conversation is rooted in real-world engineering experience.
              </p>

              <p className="text-gray-300 leading-relaxed">
                Whether you're building your first RAG pipeline or architecting enterprise-scale
                AI platforms, you'll find actionable insights, patterns to adopt, and pitfalls to
                avoid.
              </p>
            </div>

            {/* Host Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-off-white mb-6">Your Host</h2>
              <Card
                sx={{
                  backgroundColor: colors.bgCard,
                  borderRadius: '12px',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <div className="flex flex-col md:flex-row gap-6">
                    <img
                      src="/images/host-anand.jpg"
                      alt="Anand Rao"
                      className="w-32 h-32 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="text-2xl font-bold text-off-white mb-2">Anand Rao</h3>
                      <p className="text-cyan mb-4">Sr. Solution Architect & Builder</p>
                      <p className="text-gray-300 leading-relaxed">
                        Anand is passionate about AI-native apps, data platforms, developer
                        experience, and shipping real products. With years of experience building
                        and scaling production systems, he brings a pragmatic, code-first
                        perspective to every conversation.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Join the Conversation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-off-white mb-6">Join the Conversation</h2>
              <Card
                sx={{
                  backgroundColor: colors.bgCard,
                  borderRadius: '12px',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <p className="text-gray-300 mb-4 leading-relaxed">
                    This show is about bringing together like-minded folks who are venturing into
                    the world of applications, data, and AI. Whether you're just starting out or
                    you've been there and done that, your journey has valuable insights to share.
                  </p>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    I'm looking for people who are excited to explore real experiences—the wins,
                    the challenges, the lessons learned—and who want to go deep on the technical
                    and practical aspects of building with AI. If you have a story to tell about
                    your AI journey, I'd love to hear from you.
                  </p>
                  <Button
                    component={Link}
                    to="/contact"
                    variant="contained"
                    sx={{
                      backgroundColor: colors.info,
                      color: colors.textOnAccent,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: colors.infoHover,
                      },
                    }}
                  >
                    Interested in Being a Guest?
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Sponsorship */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <h2 className="text-3xl font-bold text-off-white mb-6">Sponsorship</h2>
              <Card
                sx={{
                  backgroundColor: colors.bgCard,
                  borderRadius: '12px',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <p className="text-gray-300 mb-4">
                    Prompted Thoughts reaches a highly engaged audience of software engineers,
                    architects, and technical leaders building AI-native applications.
                  </p>
                  <p className="text-gray-300 mb-6">
                    If your product or service serves this community, let's talk about sponsorship
                    opportunities.
                  </p>
                  <Button
                    component={Link}
                    to="/contact"
                    variant="outlined"
                    sx={{
                      borderColor: colors.secondaryLight,
                      color: colors.secondaryLight,
                      fontWeight: 600,
                      borderWidth: 2,
                      '&:hover': {
                        borderColor: colors.secondaryLight,
                        backgroundColor: 'rgba(var(--color-accent-secondary-rgb), 0.1)',
                        borderWidth: 2,
                      },
                    }}
                  >
                    Explore Sponsorship
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </>
  )
}

export default About
